import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    output,
    Signal,
    signal,
    viewChild,
} from '@angular/core';
import { form, FormField, readonly, submit, validate } from '@angular/forms/signals';
import { SelectFieldComponent, TextFieldComponent } from '@evorait/pace-components';
import {
    addAddressesRequested,
    AddressEdit,
    AddressState,
    cityWithUsState,
    ConfigConstants,
    deleteAddressRequested,
    editAddressRequested,
    getUuidV4,
    hideAddressRequested,
    initialAddressState,
    inputFieldLength,
    InputFieldPropertyState,
    isUsableAddress,
    isUserCreatedAddress,
    manualAddrNumber,
    requiredText,
    resetAddressVisibility,
    selectEnableHideTimesheetAddress,
    selectEnableStatesOfUSADropdown,
    selectHasHiddenAddresses,
    selectInputFieldPropertyMatch,
    selectStatesOfUSAOptions,
    selectTimesheetAddressChoices,
    selectUserCountry,
    TimesheetAddressState,
} from '@evorait/pace-core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy, faEyeSlash, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { countryName, matchesAddressSearch } from './address-search';

const emptyNewAddress: AddressEdit = {
    name1: '',
    street: '',
    city: '',
    postCode: '',
    country: '',
    stateOfUSA: '',
    addrText: '',
};

/**
 * The fields the hand-entry form carries, lifted off a row already in the address book — shared by
 * duplicating and editing so neither writes the same field list twice.
 *
 * `name1` is carried but not typed: the form does not offer it (the backend has no field for it on
 * a timesheet address), yet dropping it here would strip a duplicated customer address of the very
 * name the list identifies it by.
 *
 * To give the user a chance to change the information contained in name1, we copy it to the
 * addrText field if the address came from the backend (identified by an existing addrNumber) and
 * if the existing addrText is empty.
 *
 * From there it can take over the UI and be transmitted to the backend.
 *
 * The country is not taken from the row either. A timesheet address always carries the user's own
 * country — `TimesheetAddressModel`'s constructor overwrites it on the way out, and
 * `toJsonTimesheetAddress` does the same on the way to the submit model — so a row copied from a
 * differently addressed customer would otherwise show a country nothing downstream would honor.
 *
 * @param address the row the form is being pre-filled from
 * @param userCountry the country every address typed here carries
 * @returns the editable fields of that address
 */
function toDraft(address: AddressState, userCountry: string): AddressEdit {
    return {
        name1: address.name1,
        street: address.street,
        city: address.city,
        postCode: address.postCode,
        country: userCountry,
        stateOfUSA: address.stateOfUSA ?? '',
        addrText: address.addrNumber && !address.addrText ? address.name1 : address.addrText,
    };
}

/**
 * The draft as it is written to the store: every field trimmed.
 *
 * Leading and trailing spaces are a typing accident, not part of an address, and keeping them
 * would carry through everywhere the field is compared rather than read — `addressContentKey`
 * decides whether a hand-entered address is the one the backend later sends, and `' Erlangen'`
 * and `'Erlangen'` are two different places to it. They would also be sent to the backend as
 * typed.
 *
 * @param draft the form's current value
 * @returns the draft with no field carrying surrounding whitespace
 */
function trimDraft(draft: AddressEdit): AddressEdit {
    return {
        name1: draft.name1.trim(),
        street: draft.street.trim(),
        city: draft.city.trim(),
        postCode: draft.postCode.trim(),
        country: draft.country.trim(),
        stateOfUSA: draft.stateOfUSA.trim(),
        addrText: draft.addrText.trim(),
    };
}

/** What the open hand-entry form is doing: a fresh address, a copy of one, or a change to one. */
type AddressFormMode = { kind: 'new' } | { kind: 'duplicate' } | { kind: 'edit'; id: string };

/**
 * Picks the address a manual timesheet entry is booked against.
 *
 * Reads the address slice directly and dispatches the address actions that already exist, so the
 * page above it does not relay address concerns. The one thing it hands back is the chosen address:
 * that belongs to the timesheet form, and the page writes it into the draft rather than the picker
 * committing it — the entry's address is committed with the rest of the timesheet.
 *
 * Shown through `BsModalService`, matching how the rest of the app presents modals.
 */
@Component({
    selector: 'pace-address-picker',
    standalone: true,
    imports: [BsDropdownModule, FaIconComponent, FormField, SelectFieldComponent, TextFieldComponent, TranslateModule],
    templateUrl: './address-picker.component.html',
    styleUrl: './address-picker.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressPickerComponent {
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);

    /** the address the user chose */
    public readonly addressSelected = output<string>();

    public readonly modalInstance = inject(BsModalRef);

    /** The addresses on offer, each carrying whether it may be hidden. */
    protected readonly addresses = this.store.selectSignal(selectTimesheetAddressChoices);

    /** Whether hiding and deleting addresses is offered at all. */
    protected readonly enableHiding = this.store.selectSignal(selectEnableHideTimesheetAddress);

    /**
     * Whether an address carries a US state, and so whether the form offers the state dropdown —
     * the same flag `AddressModel.updateStateOfUSA` reads before building that dropdown at all.
     */
    protected readonly enableStatesOfUSA = this.store.selectSignal(selectEnableStatesOfUSADropdown);

    /** The states the dropdown offers, from the value help. */
    protected readonly statesOfUSAOptions = this.store.selectSignal(selectStatesOfUSAOptions);

    /**
     * The country every address typed here carries: the selected user's own, never typed and never
     * taken from a copied row — see {@link toDraft}.
     */
    protected readonly userCountry = this.store.selectSignal(selectUserCountry);

    /**
     * The form's country as the user reads it: the full name, or the code itself when it has no
     * name. The draft keeps the code — only the display changes.
     */
    protected readonly countryDisplayName = computed(() => {
        const code = this.newAddress().country;
        return countryName(code, this.translate.currentLang || 'en') ?? code;
    });

    /** Whether restoring hidden addresses would do anything. */
    protected readonly hasHiddenAddresses = this.store.selectSignal(selectHasHiddenAddresses);

    /** The free-text search above the table, matched with {@link matchesAddressSearch}. */
    protected readonly searchTerm = signal('');

    /**
     * The rows the table actually renders: {@link addresses} narrowed by the search term. The
     * template iterates over this rather than over `addresses()` directly, so the search box above
     * the table always agrees with what is shown below it.
     *
     * One list, whatever an address's origin: the picker used to offer a local/address-set filter
     * and mark the local rows, which users read as a distinction they had to understand before
     * picking an address. Where an address came from still decides what may be done *to* it
     * (see {@link isEditable} and {@link canDelete}), but it is not a way of finding one.
     */
    protected readonly filteredAddresses = computed(() => {
        const searchTerm = this.searchTerm();
        const stateNames = new Map(this.statesOfUSAOptions().map((option) => [option.value, option.label]));
        const locale = this.translate.currentLang || 'en';

        return this.addresses().filter((address) =>
            matchesAddressSearch(
                address,
                searchTerm,
                // The address stores only the codes; the user reads (and so searches for) the names.
                [stateNames.get(address.stateOfUSA ?? ''), countryName(address.country, locale)].filter(
                    (name): name is string => !!name,
                ),
            ),
        );
    });

    protected readonly faCopy = faCopy;
    protected readonly faEyeSlash = faEyeSlash;
    protected readonly faPencil = faPencilAlt;
    protected readonly faTrashAlt = faTrashAlt;

    /** Monotonic counter correlating a dispatched address write with its outcome. */
    private writeCounter = 0;

    /** What the open hand-entry form is doing; `undefined` while it is closed. */
    protected readonly formMode = signal<AddressFormMode | undefined>(undefined);

    /** The hand-entry form's section, present only while the form is open. */
    private readonly formSection = viewChild<ElementRef<HTMLElement>>('addressForm');

    /**
     * Brings the form into view as it opens.
     *
     * It is rendered below the list, and the list is as long as the address book — on a full one
     * the form opens past the bottom of the scrolling modal body, so pressing "New" looks like it
     * did nothing. Written against the form's presence rather than in the three handlers that open
     * it, so duplicating and editing are carried along with adding.
     *
     * `block: 'nearest'` scrolls as little as it takes, which leaves the list where it is whenever
     * the form is already visible.
     *
     * An `afterRenderEffect` rather than a plain `effect`: as a plain effect this ran while change
     * detection was still in progress, asking to scroll to a form the browser had not laid out yet,
     * inside a container whose scroll range had not grown to hold it. Chrome and Firefox absorbed
     * that; Edge scrolled nowhere the first time the form was opened, and behaved only once the
     * form had been rendered once already. Waiting for the render fixes it in Edge too — the smooth
     * animation was never the problem, and is kept. `mixedReadWrite` is the honest phase, since
     * `scrollIntoView` reads layout and writes the scroll position in one call.
     */
    private readonly revealForm = afterRenderEffect({
        mixedReadWrite: () =>
            this.formSection()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    });

    /**
     * Whether the open form is changing an address rather than adding one — the one distinction the
     * form's own heading and its confirm button make, since a duplicate is added like a fresh
     * address.
     */
    protected readonly isEditingAddress = computed(() => this.formMode()?.kind === 'edit');

    protected readonly newAddress = signal<AddressEdit>({ ...emptyNewAddress });

    /** The backend's input field properties for the typed fields, carrying their length bounds. */
    private readonly streetProperty = this.timesheetFieldProperty('Street');
    private readonly postCodeProperty = this.timesheetFieldProperty('PostCode');
    private readonly cityProperty = this.timesheetFieldProperty('City');
    private readonly addrTextProperty = this.timesheetFieldProperty('AddrText');

    /**
     * The hand-entry form.
     *
     * Street, postal code and city are each required, and nothing else is — the three fields
     * `TimesheetAddressModel.isValid` has always demanded before an address may be booked against an
     * entry. Free text is a note beside them, never an address on its own.
     *
     * The typed fields are bounded by the lengths the backend configures for them on the `Timesheet`
     * screen, the bounds `TimesheetAddressModel` loads for the legacy form: they are the backend's to
     * set, not this form's, and an address longer than the backend accepts would only fail once the
     * timesheet is submitted. The country is left unbounded — it is read-only, so a bound it broke
     * would block saving with nothing the user could change.
     *
     * `requiredText` rather than `required`, so that a field of spaces is refused the way an empty
     * one is: it satisfies the native check, yet names no place and would reach the backend blank.
     *
     * The root rule states the same check as a whole, so the form and the `addressRequired` rule the
     * timesheet applies to the chosen address cannot drift apart: an address this form accepted must
     * never be one the timesheet then reports as missing.
     *
     * The country is shown but never typed: it is the user's own, which is what a timesheet address
     * carries whatever the form holds, so offering it for editing would only invite a change nothing
     * downstream would honor.
     */
    protected readonly newAddressForm = form(this.newAddress, (path) => {
        validate(path, (context) =>
            isUsableAddress({ ...initialAddressState, ...context.value() })
                ? undefined
                : {
                      kind: 'addressRequired',
                      message: this.message('TIMESHEET_ADDRESS_FORM_RULE_LABEL'),
                  },
        );

        requiredText(path.street, { message: this.fieldMessage('TIMESHEET_ADDRESS_STREET_TEXT') });
        requiredText(path.postCode, { message: this.fieldMessage('TIMESHEET_ADDRESS_POSTCODE_TEXT') });
        requiredText(path.city, { message: this.fieldMessage('TIMESHEET_ADDRESS_CITY_TEXT') });

        const lengthMessages = {
            minLengthMessage: this.message('ERROR_MINLENGTH_NOT_REACHED'),
            maxLengthMessage: this.message('ERROR_MAXLENGTH_EXCEEDED'),
        };
        inputFieldLength(path.street, this.streetProperty, lengthMessages);
        inputFieldLength(path.postCode, this.postCodeProperty, lengthMessages);
        inputFieldLength(path.city, this.cityProperty, lengthMessages);
        inputFieldLength(path.addrText, this.addrTextProperty, lengthMessages);

        readonly(path.country);
    });

    /**
     * Takes the typed search text off the input event.
     *
     * The cast lives here rather than in the template, matching `SignalStringFieldBaseDirective`'s
     * own `onInput`: nothing else in this app reaches for `$any` in a template, and a typed handler
     * keeps the template checkable.
     *
     * @param event the input event the search field raised
     */
    protected onSearchInput(event: Event): void {
        this.searchTerm.set((<HTMLInputElement>event.target).value);
    }

    /** Opens the hand-entry form on a blank address, carrying the user's country. */
    protected startAddingAddress(): void {
        this.newAddress.set({ ...emptyNewAddress, country: this.userCountry() });
        this.formMode.set({ kind: 'new' });
    }

    /**
     * Opens the hand-entry form pre-filled from an existing row, as a copy: saving adds it as a new,
     * independent address rather than changing the one it was copied from — see {@link saveAddress}.
     *
     * @param address the row to copy
     */
    protected startDuplicatingAddress(address: AddressState): void {
        this.newAddress.set(toDraft(address, this.userCountry()));
        this.formMode.set({ kind: 'duplicate' });
    }

    /**
     * Opens the hand-entry form pre-filled from an existing row, to change it in place: saving keeps
     * the row's identity — see {@link saveAddress} and {@link isEditable}.
     *
     * @param address the row to change
     */
    protected startEditingAddress(address: AddressState): void {
        this.newAddress.set(toDraft(address, this.userCountry()));
        this.formMode.set({ kind: 'edit', id: address.id });
    }

    protected closeForm(): void {
        this.formMode.set(undefined);
    }

    /**
     * Saves the hand-entry form, as whatever it was opened to do.
     *
     * `submit` skips the action while the form is invalid, so an unusable address never reaches the
     * store, in every mode alike.
     *
     * Editing targets the address the form was opened on by its id, keeping its identity — see
     * `editAddressRequested` for why an edit cannot be matched by content the way a new or duplicated
     * address is.
     *
     * Adding — for a fresh address and for a duplicate alike — marks the address as not coming from
     * the backend, which is what makes it deletable rather than hideable, and gives it a fresh session
     * id because that — not the empty `addrKey` every hand-entered address shares — is what addresses
     * it. A duplicate therefore never carries the id, `addrKey` or `addrNumber` of the row it was
     * copied from: the draft holds only the six form fields, and building the new address from
     * `initialAddressState` supplies the rest fresh.
     *
     * Its address number is minted from that id: the backend has never seen this address and cannot
     * have given it one, and every address in the slice carries a number (see `manualAddrNumber`).
     * The next load that finds this address at the backend replaces it with the real one.
     *
     * What is written is the trimmed draft, in both modes — see {@link trimDraft}.
     *
     * @returns whether the form was valid and the address was therefore saved
     */
    protected saveAddress(): Promise<boolean> {
        return submit(this.newAddressForm, () => {
            const mode = this.formMode();
            const draft = trimDraft(this.newAddress());
            if (mode?.kind === 'edit') {
                this.store.dispatch(
                    editAddressRequested({
                        id: mode.id,
                        changes: draft,
                        correlationId: this.nextCorrelationId('edit'),
                    }),
                );
            } else {
                const id = getUuidV4();
                const address: AddressState = {
                    ...initialAddressState,
                    ...draft,
                    addrNumber: manualAddrNumber(id),
                    visible: true,
                    isFromAddressSet: false,
                    allowToHide: false,
                    id,
                };
                this.store.dispatch(
                    addAddressesRequested({ addresses: [address], correlationId: this.nextCorrelationId('add') }),
                );
                // A freshly added address could otherwise land outside an active search and never
                // be seen. An edit gets no such reset: the user is searching on purpose, and losing
                // that context on every save would be more disruptive than helpful.
                this.searchTerm.set('');
            }
            this.closeForm();
            return Promise.resolve(undefined);
        });
    }

    /**
     * Hands the chosen address to the timesheet form and closes.
     *
     * Only the address fields a timesheet entry carries are taken; the contact and naming fields an
     * `AddressState` also holds are the address book's business, not the entry's.
     *
     * @param address the address the user picked
     */
    protected select(address: AddressState): void {
        this.addressSelected.emit(address.id);
        this.modalInstance.hide();
    }

    /**
     * Hides an address from the list without losing it — restorable through {@link resetVisibility}.
     *
     * Addressed by `id`, not by `addrKey`: every manually created address shares the empty
     * key, so it cannot single one out.
     *
     * @param address the address to hide
     */
    protected hide(address: AddressState): void {
        this.store.dispatch(hideAddressRequested({ id: address.id, correlationId: this.nextCorrelationId('hide') }));
    }

    /**
     * @param address the address to delete for good
     */
    protected delete(address: AddressState): void {
        this.store.dispatch(
            deleteAddressRequested({ id: address.id, correlationId: this.nextCorrelationId('delete') }),
        );
    }

    /**
     * Whether the address may be changed in place: one only this device knows.
     *
     * A backend address's fields are the backend's to say, and the next load would fold the
     * backend's version straight back over an edit (see `editAddressRequested`). `isFromAddressSet`
     * alone is not enough to tell such an address apart: a notification's long customer form is not
     * from the address set yet still carries a backend address number. This is the rule the legacy
     * `AddressSelectComponent` used, and it is the same rule that decides whether the row offers
     * "Delete" (see {@link canDelete}) rather than "Hide".
     *
     * @param address the row to test
     */
    protected isEditable(address: AddressState): boolean {
        return isUserCreatedAddress(address);
    }

    /**
     * Whether the row offers "Delete" rather than "Hide" — only an address this device typed by
     * hand, never a customer address or an `AddressSet` row. Deleting either backend form would
     * strand it the moment the timesheet or a submitted entry still points at it; hiding does not,
     * since a backend re-send (`upsertAddress`) or a restore (`resetAddressVisibility`) brings it
     * back. A user-created address has no such backend copy to fall back to, so it is deleted
     * outright instead of offered a hide it cannot recover from meaningfully.
     *
     * @param address the row to test
     */
    protected canDelete(address: AddressState): boolean {
        return isUserCreatedAddress(address);
    }

    /** Brings every hidden address back. */
    protected resetVisibility(): void {
        this.store.dispatch(resetAddressVisibility({ correlationId: this.nextCorrelationId('reset') }));
    }

    /**
     * @param operation names the write in the correlation id, so a log line says which one it was
     * @returns a correlation id unique to this picker instance
     */
    private nextCorrelationId(operation: string): string {
        return `timesheet-address-${operation}-${++this.writeCounter}-${getUuidV4()}`;
    }

    /**
     * The input field property of a typed field, looked up the way `TimesheetAddressModel` does
     * through `ModelOptionsLoaderService.load`: on the `Timesheet` screen, for the default process.
     *
     * @param fieldName the backend's name for the field
     * @returns the field's property, `undefined` while the backend has none for it
     */
    private timesheetFieldProperty(fieldName: string): Signal<InputFieldPropertyState | undefined> {
        const match = this.store.selectSignal(
            selectInputFieldPropertyMatch({
                fieldName,
                screenName: 'Timesheet',
                process: ConfigConstants.PROCESS_INDICATOR.INVIVO,
            }),
        );
        return computed(() => match().property);
    }

    /**
     * @param labelKey the field's label key
     * @returns the message naming the field that is still missing
     */
    private fieldMessage(labelKey: string): string {
        return <string>this.translate.instant('TIMESHEET_ADDRESS_FIELD_REQUIRED_LABEL', {
            field: this.translate.instant(labelKey),
        });
    }

    /**
     * @param key the translation key
     * @returns the resolved message
     */
    private message(key: string): string {
        return <string>this.translate.instant(key);
    }

    protected cityWithUsState(address: TimesheetAddressState): string | undefined {
        return cityWithUsState(address);
    }
}
